import {
  searchProvidersWithMeta,
  countProvidersWithFilters,
  parseAiQuery,
  findProvidersByIdentifier,
  parseLocationToCityState,
  SPECIALTIES,
  CITIES,
  type Provider,
  type SearchMeta,
} from "@/data/heallexa";
import { logger } from "@/lib/logger.browser";

// Lifted out of routes/search.tsx so /providers/$id and /providers/$state/$city
// can run the exact same AI-query parsing, identifier lookup, and loose-city
// fallback that /search already has, instead of a thinner per-route fetch.

export const SEARCH_RESULT_LIMIT = 20;

const US_STATE_NAME_TO_CODE = new Map<string, string>([
  ["alabama","AL"],["alaska","AK"],["arizona","AZ"],["arkansas","AR"],
  ["california","CA"],["colorado","CO"],["connecticut","CT"],["delaware","DE"],
  ["florida","FL"],["georgia","GA"],["hawaii","HI"],["idaho","ID"],
  ["illinois","IL"],["indiana","IN"],["iowa","IA"],["kansas","KS"],
  ["kentucky","KY"],["louisiana","LA"],["maine","ME"],["maryland","MD"],
  ["massachusetts","MA"],["michigan","MI"],["minnesota","MN"],["mississippi","MS"],
  ["missouri","MO"],["montana","MT"],["nebraska","NE"],["nevada","NV"],
  ["new hampshire","NH"],["new jersey","NJ"],["new mexico","NM"],["new york","NY"],
  ["north carolina","NC"],["north dakota","ND"],["ohio","OH"],["oklahoma","OK"],
  ["oregon","OR"],["pennsylvania","PA"],["rhode island","RI"],["south carolina","SC"],
  ["south dakota","SD"],["tennessee","TN"],["texas","TX"],["utah","UT"],
  ["vermont","VT"],["virginia","VA"],["washington","WA"],["west virginia","WV"],
  ["wisconsin","WI"],["wyoming","WY"],["district of columbia","DC"],
]);
const US_STATE_CODES = new Set<string>(Array.from(US_STATE_NAME_TO_CODE.values()));

function emptyMeta(radius: number | undefined): SearchMeta {
  return {
    requestedRadiusMiles: radius && radius > 0 ? Math.min(radius, 50) : 0,
    usedRadius: false,
    centroid: null,
  };
}

/**
 * "near me" and its cousins are an intent, not a place — but every parser below
 * reads them as one, and both readings are wrong in the same query:
 *
 *   "dentists near me"
 *     extractLocationFromSearchText -> /\b(?:in|near|around)\s+(.+)$/ captures
 *       "me", so _city = 'me' and matches zero providers.
 *     inferStateFromText -> /\b([A-Za-z]{2})\b\s*$/ captures "ME", a real state
 *       code, so the search is scoped to MAINE.
 *   The q branch has no empty-result fallbacks, so the visitor gets Maine
 *   dentists or nothing at all — for the single most common way anyone phrases
 *   a proximity search.
 *
 * Stripping the phrase once, up front, fixes both: the remaining text parses as
 * "dentists" with no location, and the actual proximity comes from deps.lat/lng
 * (the device coords the route already puts in the URL) exactly as it does for
 * a search with no location typed at all.
 */
export function stripNearMe(text: string): string {
  return text
    .replace(/\b(?:near(?:by|est)?|around|close\s+to|closest\s+to)\s+(?:me|here|my\s+(?:location|area|place))\b/gi, " ")
    .replace(/\bnear\s*by\b/gi, " ")
    // Split forms the pattern above cannot see: "nearest cardiologist TO ME".
    // A trailing bare "me" is the dangerous half (it reads as Maine), and only
    // a preposition can precede it here — "Portland, ME" ends in a comma, so it
    // survives.
    .replace(/\b(?:to|for|from)\s+me\b\s*$/i, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function inferStateFromText(text: string | undefined): string | undefined {
  if (!text) return undefined;
  const m = text.match(/\b([A-Za-z]{2})\b\s*$/);
  if (m) {
    const code = m[1].toUpperCase();
    if (US_STATE_CODES.has(code)) return code;
  }
  const lower = ` ${text.toLowerCase()} `;
  for (const [name, code] of US_STATE_NAME_TO_CODE) {
    if (lower.includes(` ${name} `)) return code;
  }
  return undefined;
}

function extractLocationFromSearchText(text: string): string | undefined {
  const raw = text.trim().replace(/\s+/g, " ");
  const explicit = raw.match(/\b(?:in|near|around)\s+([^?]+)$/i)?.[1];
  const candidate = (explicit || "")
    .replace(/\b(?:who|that)?\s*(?:accepts?|take|takes|with|for|open|available)\b.*$/i, "")
    .replace(/[.!?]+$/g, "")
    .trim();
  if (candidate) return candidate;
  const knownCity = CITIES.find((c) => raw.toLowerCase().includes(c.split(",")[0].toLowerCase()));
  return knownCity || undefined;
}

function extractSpecialtyFromSearchText(text: string): string | undefined {
  const lower = text.toLowerCase();
  return SPECIALTIES.find((s) => {
    const label = s.toLowerCase();
    const primary = label.split("/")[0].trim();
    const singular = primary.replace(/s\b/, "");
    return lower.includes(label) || lower.includes(primary) || lower.includes(singular);
  });
}

// Query strings may arrive with `+` representing space (form-encoded URLs from
// external links, share cards, IndexNow rewrites). decodeURIComponent does NOT
// translate `+` → space, so we must clean text params explicitly before they
// reach the database. Without this, `location=Fort+Worth%2C+TX` becomes
// "Fort+Worth, TX" and `_city = 'Fort+Worth'` matches zero providers.
export function cleanQueryText(v: unknown): string | undefined {
  // The router's search-param parser types `?location=77002` as the NUMBER
  // 77002, not a string. Rejecting non-strings here dropped `location`
  // entirely, so validateSearch produced a different search object than the URL
  // and the router 307'd `/search?location=77002` -> `/search` — every ZIP
  // search silently became an unfiltered nationwide one. Any scalar is text
  // here, so coerce instead of discarding.
  if (typeof v === "number" || typeof v === "boolean") v = String(v);
  if (typeof v !== "string") return undefined;
  let s = v;
  // Some upstream callers double-encode; try one defensive decode pass.
  if (/%[0-9A-Fa-f]{2}/.test(s)) {
    try { s = decodeURIComponent(s); } catch { /* keep original */ }
  }
  s = s.replace(/\+/g, " ").replace(/\s+/g, " ").trim();
  return s || undefined;
}

/**
 * Does this free-text query name a place?
 *
 * The routes need to know, because device coordinates must never override a
 * location the visitor actually asked for. The location FIELD is easy to check,
 * but a place named inside the query text — "dentists in Boston" — was invisible
 * to that check, so a visitor in New York searching for Boston dentists had the
 * radius centre silently moved to New York. The banner still read "Within 25 mi
 * of Boston" (meta.city comes from the parsed text) and the client-side city
 * filter still demanded city = Boston, so every New York result it fetched was
 * then discarded: 1,953 providers with no coordinates, zero with them.
 *
 * Deliberately the SAME extraction the loader itself runs below, so the client's
 * "is there a location here?" answer cannot drift from the server's "here is the
 * location I parsed". stripNearMe first, so "dentists near me" is correctly seen
 * as naming no place at all — that query SHOULD use the visitor's coordinates.
 */
export function queryNamesLocation(q: string | undefined): boolean {
  if (!q) return false;
  const text = stripNearMe(q);
  if (!text) return false;
  return Boolean(extractLocationFromSearchText(text) || parseAiQuery(text).location);
}

export type SearchLoaderDeps = {
  specialty?: string;
  location?: string;
  insurance?: string;
  q?: string;
  radius?: number;
  page?: number;
  // Device geolocation ("near me"): distance-scoped results + per-provider distance.
  lat?: number;
  lng?: number;
  // Set by the /providers/... landing pages, whose location is part of the URL,
  // the <h1>, and the canonical. Their scope must stay the city they name, so
  // the visitor's coordinates are used ONLY to stamp per-provider distance and
  // enable nearest-first sorting within that city — never to move the centre.
  distanceOnly?: boolean;
};

export type SearchLoaderResult = {
  results: Provider[];
  page: number;
  hasMore: boolean;
  total: number;
  totalCapped: boolean;
  filters: { specialty: string; location: string; insurance: string };
  aiQuery: string | null;
  ai: null;
  meta: SearchMeta;
};

export async function runProviderSearch(deps: SearchLoaderDeps): Promise<SearchLoaderResult> {
  const page = Math.max(1, Number(deps.page) || 1);
  const offset = (page - 1) * SEARCH_RESULT_LIMIT;
  // Same coordinates, two different jobs — see SearchLoaderDeps.distanceOnly.
  const geoArgs = deps.distanceOnly
    ? { distanceLat: deps.lat, distanceLng: deps.lng }
    : { lat: deps.lat, lng: deps.lng };
  // Fetch PAGE_SIZE + 1 to detect whether a "next" page exists without
  // running an expensive COUNT(*) over the 6M-row providers table.
  const fetchLimit = SEARCH_RESULT_LIMIT + 1;

  if (deps.q) {
    // Parse the query with the proximity phrase removed — see stripNearMe. The
    // untouched deps.q is still what gets echoed back to the visitor and sent
    // to the AI parser; only the location/state/specialty extraction below runs
    // on the stripped text, because those are the parsers it fools.
    const qText = stripNearMe(deps.q);
    const parsedLocal = parseAiQuery(qText);
    const explicitLocation = extractLocationFromSearchText(qText) || parsedLocal.location || deps.location;
    const explicitSpecialty = extractSpecialtyFromSearchText(qText) || parsedLocal.specialty;
    const localParsedLoc = parseLocationToCityState(explicitLocation);
    const inferredState =
      localParsedLoc.state ||
      inferStateFromText(qText) ||
      parseLocationToCityState(deps.location).state;

    const baseCity =
      (deps.radius && deps.location)
        ? deps.location
        : explicitLocation;
    const baseParsed = parseLocationToCityState(baseCity);
    const composedLocation = baseParsed.city
      ? (baseParsed.state
          ? `${baseParsed.city}, ${baseParsed.state}`
          : (inferredState ? `${baseParsed.city}, ${inferredState}` : baseParsed.city))
      : baseCity;

    const tokens = Array.from(
      new Set(
        (deps.q.match(/\b[A-Za-z0-9-]{4,20}\b/g) || []).filter((tok) => {
          if (/^\d{10}$/.test(tok)) return true;
          if (/^[A-Za-z]{0,4}\d{3,}[A-Za-z0-9-]*$/.test(tok)) return true;
          return false;
        }),
      ),
    ).slice(0, 3);

    const hasLocalFilters = !!(explicitSpecialty || explicitLocation || parsedLocal.insurance);
    // qText, not deps.q: a bare "near me" strips to "" and must not become a
    // free-text ILIKE '%near me%' over provider names.
    const structuredQ = hasLocalFilters ? undefined : (qText || undefined);

    const baseArgs = {
      q: structuredQ,
      specialty: explicitSpecialty || undefined,
      location: composedLocation,
      state: inferredState,
      insurance: undefined, // ranked client-side, never a SQL predicate — see note below
      // `undefined`, not 0 — an explicit 0 means "no distance scoping" and
      // would suppress the ZIP/near-me default radius (see defaultRadiusMiles).
      radiusMiles: deps.radius,
      ...geoArgs,
    };

    const [primary, totalRes, ...idResults] = await Promise.all([
      searchProvidersWithMeta({
        ...baseArgs,
        limit: fetchLimit,
        offset,
      }).catch(() => ({ results: [] as Provider[], meta: emptyMeta(deps.radius) })),
      countProvidersWithFilters(baseArgs).catch(() => ({ count: 0, capped: false })),
      // Identifier lookup only on page 1 (it's a small global lookup, not paged).
      ...(page === 1
        ? tokens.map((t) => findProvidersByIdentifier(t, 8).catch(() => [] as Provider[]))
        : []),
    ]);

    const hasMore = primary.results.length > SEARCH_RESULT_LIMIT;
    const pageRows = primary.results.slice(0, SEARCH_RESULT_LIMIT);

    const seen = new Set<string>();
    const merged: Provider[] = [];
    for (const list of [...idResults, pageRows]) {
      for (const p of list) {
        if (!seen.has(p.id)) { seen.add(p.id); merged.push(p); }
      }
    }
    return {
      results: merged.slice(0, SEARCH_RESULT_LIMIT),
      page,
      hasMore,
      total: totalRes.count,
      totalCapped: totalRes.capped,
      filters: {
        specialty: explicitSpecialty || "",
        location: composedLocation || "",
        // Explicit pick beats the guess parsed out of the query text — this
        // used to echo only parsedLocal, so choosing a plan in the picker and
        // then typing anything into the search box silently cleared it.
        insurance: deps.insurance || parsedLocal.insurance || "",
      },
      aiQuery: deps.q,
      ai: null,
      meta: primary.meta,
    };
  }

  const filters = {
    specialty: deps.specialty || "",
    location: deps.location || "",
    insurance: deps.insurance || "",
  };
  const depsParsed = parseLocationToCityState(deps.location);
// Insurance is deliberately NOT sent to the search RPC.
//
// It used to be a WHERE predicate, which was both slow and wrong for the UI we
// want. Slow: `insurance @> ARRAY[token]` matches very few of the 6.18M rows,
// so with ORDER BY rating + LIMIT the planner walked the rating index hunting
// for matches that mostly do not exist and blew the 5s statement_timeout on
// EVERY insurance search. Wrong: filtering hides out-of-network providers and,
// worse, the millions of listings whose insurance[] is simply empty — the
// patient could not tell "does not accept" from "we have not asked them".
//
// The token now only classifies and orders what the normal search returns
// (in-network -> out-of-network -> unknown), so the expensive predicate is
// never planned. See insuranceStatusFor() in lib/insurance-catalog.functions.
  const baseArgs = {
    ...filters,
    insurance: "",
    state: depsParsed.state,
    radiusMiles: deps.radius,
    ...geoArgs,
  };
  const [resultsRes, countRes] = await Promise.all([
    searchProvidersWithMeta({ ...baseArgs, limit: fetchLimit, offset })
      .catch((e) => {
        logger.error("search loader: searchProvidersWithMeta failed", {
          ...filters, radius: deps.radius || 0, page,
          error: e instanceof Error ? { message: e.message, stack: e.stack } : e,
        });
        return { results: [] as Provider[], meta: emptyMeta(deps.radius) };
      }),
    countProvidersWithFilters(baseArgs).catch(() => ({ count: 0, capped: false })),
  ]);
  const { results, meta } = resultsRes;

  let hasMore = results.length > SEARCH_RESULT_LIMIT;
  let finalResults = results.slice(0, SEARCH_RESULT_LIMIT);
  let finalMeta = meta;
  let total = countRes.count;
  let totalCapped = countRes.capped;

  // Fallback: a geo-scoped ("near me") search that comes up empty is most
  // likely a sparse/rural area, not zero doctors — widen the radius once
  // before giving up. Real medical search, so a dead-end empty state is
  // worse than a wider result set. 50mi is the system-wide ceiling — see
  // the `_radius_miles` clamp in search-providers.functions.ts — so there's
  // nothing left to widen to beyond it.
  const RADIUS_CEILING_MILES = 50;
  // Keyed on the radius the search ACTUALLY ran with (meta), not on deps.lat —
  // a ZIP search derives its centre from the ZIP centroid, so it has no
  // deps.lat/lng and would otherwise skip straight past this widen step.
  if (page === 1 && finalResults.length === 0 && meta.usedRadius &&
      meta.requestedRadiusMiles < RADIUS_CEILING_MILES) {
    try {
      const wideArgs = { ...baseArgs, radiusMiles: RADIUS_CEILING_MILES };
      const [wide, wideCount] = await Promise.all([
        searchProvidersWithMeta({ ...wideArgs, limit: fetchLimit, offset: 0 }),
        countProvidersWithFilters(wideArgs).catch(() => ({ count: 0, capped: false })),
      ]);
      if (wide.results.length > 0) {
        hasMore = wide.results.length > SEARCH_RESULT_LIMIT;
        finalResults = wide.results.slice(0, SEARCH_RESULT_LIMIT);
        finalMeta = wide.meta;
        total = wideCount.count;
        totalCapped = wideCount.capped;
      }
    } catch { /* keep empty, fallbacks below still run */ }
  }

  // Final fallback: a geo-only search (no typed location, so the city
  // fallback below can't help) that's still empty even at the radius
  // ceiling is a true desert — drop the distance scoping entirely rather
  // than dead-end on "No providers found" for a real medical search.
  if (page === 1 && finalResults.length === 0 && deps.lat != null && deps.lng != null &&
      !deps.location) {
    try {
      const nationwideArgs = { ...filters, state: depsParsed.state, radiusMiles: 0 };
      const [nationwide, nationwideCount] = await Promise.all([
        searchProvidersWithMeta({ ...nationwideArgs, limit: fetchLimit, offset: 0 }),
        countProvidersWithFilters(nationwideArgs).catch(() => ({ count: 0, capped: false })),
      ]);
      if (nationwide.results.length > 0) {
        hasMore = nationwide.results.length > SEARCH_RESULT_LIMIT;
        finalResults = nationwide.results.slice(0, SEARCH_RESULT_LIMIT);
        finalMeta = nationwide.meta;
        total = nationwideCount.count;
        totalCapped = nationwideCount.capped;
      }
    } catch { /* keep empty, loose city fallback below still runs */ }
  }

  // Fallback: only on page 1, if exact city/state returned nothing, retry
  // with a looser city-only query so users never land on a blank slate.
  if (page === 1 && finalResults.length === 0 && (depsParsed.city || deps.location)) {
    try {
      const looseArgs = {
        specialty: filters.specialty,
        location: depsParsed.city || deps.location,
        insurance: filters.insurance,
        radiusMiles: 0,
      };
      const [loose, looseCount] = await Promise.all([
        searchProvidersWithMeta({ ...looseArgs, limit: fetchLimit, offset: 0 }),
        countProvidersWithFilters(looseArgs).catch(() => ({ count: 0, capped: false })),
      ]);
      if (loose.results.length > 0) {
        hasMore = loose.results.length > SEARCH_RESULT_LIMIT;
        finalResults = loose.results.slice(0, SEARCH_RESULT_LIMIT);
        finalMeta = loose.meta;
        total = looseCount.count;
        totalCapped = looseCount.capped;
      }
    } catch { /* keep empty */ }
  }
  return { results: finalResults, page, hasMore, total, totalCapped, filters, aiQuery: null, ai: null, meta: finalMeta };
}
